import React from 'react';
import { motion } from 'framer-motion';

// Badge definitions with animations
export const BADGES = {
  // Onboarding
  new_member: {
    id: 'new_member',
    name: 'Nouveau Moussaillon',
    icon: '⚓',
    color: 'from-blue-500 to-cyan-500',
    description: 'Bienvenue dans l\'équipage !',
    rarity: 'common',
    animation: 'pulse'
  },
  mousse_forme: {
    id: 'mousse_forme',
    name: 'Mousse Formé',
    icon: '🎓',
    color: 'from-green-500 to-emerald-500',
    description: 'Tutoriel terminé',
    rarity: 'common',
    animation: 'bounce'
  },
  
  // Messages
  first_message: {
    id: 'first_message',
    name: 'Premier Message',
    icon: '💬',
    color: 'from-blue-400 to-blue-600',
    description: 'A envoyé son premier message',
    rarity: 'common',
    animation: 'pulse'
  },
  first_10_messages: {
    id: 'first_10_messages',
    name: 'Bavard',
    icon: '🗣️',
    color: 'from-indigo-500 to-purple-500',
    description: '10 messages envoyés',
    rarity: 'uncommon',
    animation: 'wiggle'
  },
  centurion: {
    id: 'centurion',
    name: 'Centurion',
    icon: '🏛️',
    color: 'from-purple-500 to-pink-500',
    description: '100 messages envoyés',
    rarity: 'rare',
    animation: 'glow'
  },

  // Victories
  first_victory: {
    id: 'first_victory',
    name: 'Première Victoire',
    icon: '🏆',
    color: 'from-yellow-400 to-amber-500',
    description: 'A partagé sa première victoire',
    rarity: 'uncommon',
    animation: 'bounce'
  },
  first_property: {
    id: 'first_property',
    name: 'Propriétaire',
    icon: '🏠',
    color: 'from-amber-500 to-orange-500',
    description: 'Premier bien immobilier acquis',
    rarity: 'epic',
    animation: 'glow'
  },

  // Events
  event_first: {
    id: 'event_first',
    name: 'Premier Événement',
    icon: '📅',
    color: 'from-cyan-500 to-blue-500',
    description: 'A participé à son premier événement',
    rarity: 'common',
    animation: 'pulse'
  },
  event_10: {
    id: 'event_10',
    name: 'Fidèle',
    icon: '⭐',
    color: 'from-yellow-500 to-amber-500',
    description: '10 événements',
    rarity: 'rare',
    animation: 'spin'
  },
  event_25: {
    id: 'event_25',
    name: 'VIP',
    icon: '👑',
    color: 'from-amber-400 to-yellow-500',
    description: '25 événements',
    rarity: 'epic',
    animation: 'glow'
  },

  // Support
  supporter_bronze: {
    id: 'supporter_bronze',
    name: 'Supporter Bronze',
    icon: '🥉',
    color: 'from-orange-600 to-orange-800',
    description: '10 félicitations',
    rarity: 'common',
    animation: 'pulse'
  },
  supporter_silver: {
    id: 'supporter_silver',
    name: 'Supporter Argent',
    icon: '🥈',
    color: 'from-gray-300 to-gray-500',
    description: '25 félicitations',
    rarity: 'uncommon',
    animation: 'shine'
  },
  supporter_gold: {
    id: 'supporter_gold',
    name: 'Supporter Or',
    icon: '🥇',
    color: 'from-yellow-400 to-amber-500',
    description: '50 félicitations',
    rarity: 'rare',
    animation: 'glow'
  },

  // Season
  season_participant: {
    id: 'season_participant',
    name: 'Participant',
    icon: '🎯',
    color: 'from-blue-500 to-indigo-500',
    description: 'A rejoint une saison',
    rarity: 'common',
    animation: 'pulse'
  },
  season_champion: {
    id: 'season_champion',
    name: 'Champion de Saison',
    icon: '🏅',
    color: 'from-yellow-400 to-red-500',
    description: 'A complété tous les objectifs',
    rarity: 'legendary',
    animation: 'rainbow'
  },

  // Streaks
  streak_7: {
    id: 'streak_7',
    name: 'Semaine Parfaite',
    icon: '🔥',
    color: 'from-orange-500 to-red-500',
    description: '7 jours consécutifs',
    rarity: 'uncommon',
    animation: 'flame'
  },
  streak_30: {
    id: 'streak_30',
    name: 'Mois Parfait',
    icon: '💎',
    color: 'from-cyan-400 to-blue-500',
    description: '30 jours consécutifs',
    rarity: 'epic',
    animation: 'glow'
  },

  // Ideas
  idea_contributor: {
    id: 'idea_contributor',
    name: 'Innovateur',
    icon: '💡',
    color: 'from-yellow-400 to-orange-400',
    description: 'Idée implémentée',
    rarity: 'rare',
    animation: 'glow'
  },
};

// Rarity colors
const RARITY_COLORS = {
  common: 'border-gray-400',
  uncommon: 'border-green-500',
  rare: 'border-blue-500',
  epic: 'border-purple-500',
  legendary: 'border-amber-400'
};

// Animation variants
const animations = {
  pulse: {
    scale: [1, 1.05, 1],
    transition: { repeat: Infinity, duration: 2 }
  },
  bounce: {
    y: [0, -5, 0],
    transition: { repeat: Infinity, duration: 1.5 }
  },
  wiggle: {
    rotate: [-3, 3, -3],
    transition: { repeat: Infinity, duration: 0.5 }
  },
  spin: {
    rotate: [0, 360],
    transition: { repeat: Infinity, duration: 3, ease: 'linear' }
  },
  glow: {
    boxShadow: [
      '0 0 5px rgba(255,215,0,0.3)',
      '0 0 20px rgba(255,215,0,0.6)',
      '0 0 5px rgba(255,215,0,0.3)'
    ],
    transition: { repeat: Infinity, duration: 2 }
  },
  shine: {
    opacity: [0.7, 1, 0.7],
    transition: { repeat: Infinity, duration: 1.5 }
  },
  flame: {
    scale: [1, 1.1, 1],
    filter: ['brightness(1)', 'brightness(1.3)', 'brightness(1)'],
    transition: { repeat: Infinity, duration: 0.8 }
  },
  rainbow: {
    filter: [
      'hue-rotate(0deg)',
      'hue-rotate(360deg)'
    ],
    transition: { repeat: Infinity, duration: 3, ease: 'linear' }
  }
};

// Animated Badge Component
export const AnimatedBadge = ({ 
  badgeId, 
  size = 'md',
  showName = false,
  showAnimation = true,
  onClick 
}) => {
  const badge = BADGES[badgeId];
  if (!badge) return null;

  const sizeClasses = {
    sm: 'w-8 h-8 text-base',
    md: 'w-12 h-12 text-xl',
    lg: 'w-16 h-16 text-2xl',
    xl: 'w-24 h-24 text-4xl'
  };

  return (
    <motion.div 
      className={`relative inline-flex flex-col items-center ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div
        className={`
          ${sizeClasses[size]}
          rounded-full 
          bg-gradient-to-br ${badge.color}
          flex items-center justify-center
          border-2 ${RARITY_COLORS[badge.rarity]}
          shadow-lg
        `}
        animate={showAnimation ? animations[badge.animation] : {}}
      >
        <span className="drop-shadow-md">{badge.icon}</span>
      </motion.div>
      
      {showName && (
        <span className="mt-1 text-xs font-medium text-center max-w-[80px] truncate">
          {badge.name}
        </span>
      )}

      {/* Rarity glow for epic/legendary */}
      {(badge.rarity === 'epic' || badge.rarity === 'legendary') && showAnimation && (
        <motion.div
          className={`absolute inset-0 rounded-full bg-gradient-to-br ${badge.color} blur-md -z-10`}
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ repeat: Infinity, duration: 2 }}
        />
      )}
    </motion.div>
  );
};

// Badge Grid Component
export const BadgeGrid = ({ badges, size = 'md', showNames = true }) => {
  return (
    <div className="flex flex-wrap gap-3 justify-center">
      {badges.map((badgeId) => (
        <AnimatedBadge 
          key={badgeId} 
          badgeId={badgeId} 
          size={size}
          showName={showNames}
        />
      ))}
    </div>
  );
};

// Badge Showcase (for profile)
export const BadgeShowcase = ({ badges, maxDisplay = 5 }) => {
  const displayBadges = badges?.slice(0, maxDisplay) || [];
  const remaining = (badges?.length || 0) - maxDisplay;

  return (
    <div className="flex items-center gap-1">
      {displayBadges.map((badgeId, idx) => (
        <AnimatedBadge 
          key={badgeId} 
          badgeId={badgeId} 
          size="sm"
          showAnimation={idx === 0} // Only animate first badge
        />
      ))}
      {remaining > 0 && (
        <span className="text-xs text-muted-foreground ml-1">+{remaining}</span>
      )}
    </div>
  );
};

export default AnimatedBadge;
